import { createLive, liveUrl, type LiveAlert, type LivePosition, type LiveState, type TrackingOverview } from '@vlm/api';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { env } from '../env';
import { api, sessionStore } from './api';

interface LiveStore {
  state: LiveState;
  /** vehicleId → when its last live report arrived (ms): a marker pings once per report. */
  reports: Record<number, number>;
}

export const useLive = create<LiveStore>(() => ({ state: 'off', reports: {} }));

export const OVERVIEW_KEY = ['tracking', 'overview'] as const;

/** Every vehicle's last position and the moving/stopped/offline tally. Live pushes keep it current between fetches. */
export function useTrackingOverview() {
  const live = useLive((s) => s.state);
  return useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api.tracking.overview(),
    // OFFLINE is decided on read (no report for N minutes) and is never pushed, so refetch anyway
    refetchInterval: live === 'live' ? 60_000 : 30_000,
    staleTime: 10_000,
  });
}

/** Merges a pushed position into the overview; false when the vehicle is not in it yet. */
function applyPosition(client: QueryClient, p: LivePosition): boolean {
  let found = false;
  client.setQueryData<TrackingOverview>(OVERVIEW_KEY, (old) => {
    if (!old) return old;
    const vehicles = old.vehicles.map((v) => {
      if (v.vehicleId !== p.vehicleId) return v;
      found = true;
      return {
        ...v,
        vehicleName: p.vehicleName,
        plateNumber: p.plateNumber,
        latitude: p.latitude,
        longitude: p.longitude,
        speed: p.speed,
        direction: p.direction,
        status: p.status,
        lastUpdate: p.lastUpdate,
        deviceName: p.deviceName ?? v.deviceName,
        imei: p.imei ?? v.imei,
      };
    });
    return found ? { ...old, vehicles } : old;
  });
  return found;
}

/** One profile read at a time: it refreshes the session if the access token has run out. */
let renewing: Promise<unknown> | null = null;

/**
 * One STOMP connection for the signed-in app: positions update the map's data in place, the
 * counts update the tally, and an alert refreshes notifications and alerts and is announced.
 * Every CONNECT carries the current access token (the feed is per company, docs/SAAS.md §3).
 * When the broker turns it away (it ran out, and the broker closes the feed at that moment), one
 * API call renews the session and the reconnect, three seconds on, signs in with the new token;
 * if the session cannot be renewed, auth.ts signs the user out and this feed stops with the shell.
 */
export function useLiveFeed(onAlert: (alert: LiveAlert) => void) {
  const client = useQueryClient();
  const announce = useRef(onAlert);
  announce.current = onAlert;

  useEffect(() => {
    const live = createLive(
      liveUrl(env.apiUrl, window.location),
      {
        onState: (state) => useLive.setState({ state }),
        onPosition: (position) => {
          if (!applyPosition(client, position)) void client.invalidateQueries({ queryKey: OVERVIEW_KEY });
          useLive.setState((s) => ({ reports: { ...s.reports, [position.vehicleId]: Date.now() } }));
        },
        onCounts: (counts) => client.setQueryData<TrackingOverview>(OVERVIEW_KEY, (old) => (old ? { ...old, ...counts } : old)),
        onAlert: (alert) => {
          void client.invalidateQueries({ queryKey: ['notifications'] });
          void client.invalidateQueries({ queryKey: ['alerts'] });
          announce.current(alert);
        },
        onUnauthorized: () => {
          renewing ??= api.profile
            .get()
            .catch(() => {})
            .finally(() => {
              renewing = null;
            });
        },
      },
      () => sessionStore.get()?.accessToken ?? null,
    );
    live.start();
    return () => {
      void live.stop();
    };
  }, [client]);
}
